import * as React from 'react';
import { connect } from 'react-redux';
import { locationRecordConfigs, LocationWorkflowStepName } from './config';
import DualFormElement from '../../../DualValueFormElement/FormElement';
import MappingComponent from '../../../FormIO/MappingComponent';
import Api from '../../../../services/Api';
import { ApiResponse } from 'apisauce';
import * as urls from '../../../shared/urls';
import { eLocationUserType, IFormioValidationError, IMapConfig } from '../../../../common/sharedTypes';
import _ from 'lodash';
import { Col, Row, Grid } from 'react-flexbox-grid';
import { Row as BsRow, Col as BsCol } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import './location.css';
import { IFormElementConfig } from '../../../DualValueFormElement/assets/BusinessObjects';
import { toast } from 'react-toastify';
import TooltipToggle from '../../../shared/TooltipToggle/TooltipToggle';
import { getCitiesByCountyId } from "../../../../services/Utils/IndexedDBUtils";

interface ILocationDataEntryProps {
  itemToEdit: number;
  webserviceUrl: string;
  readOnly: boolean;
  currentUserId: number;
  userTypeId: number;
  currentQueueType: LocationWorkflowStepName;
  setCrashId: (id: string) => void;
  setContinue: (value: boolean) => void;
  workflowItemStepId: number;
  updateDataToSave: (data: any) => void;
  validationErrors?: IFormioValidationError[];
  openMap?: (mapConfig: any) => void;
  validateData?: () => void;
  handleSaveLocationEntry?: () => void;
  validatingLocationData?: boolean;
}

interface ILocationDataEntryState {
  dataToEdit: any;
  allRecords: IFormElementConfig[];
  spdCheckbox: boolean;
  spdDisabled: boolean;
  workZoneCheckbox: boolean;
  workZoneDisabled: boolean;
  privateProperty: boolean;
  unknownLocation: boolean;
}

export enum eValueType {
  CalculatedValue,
  CorrectedValue,
}

class LocationDataEntry extends React.Component<
  ILocationDataEntryProps,
  ILocationDataEntryState
> {
  mapConfig: IMapConfig;
  elementRefs: any[] = [];
  mapElement: any;

  constructor(props) {
    super(props);
    this.state = {
      dataToEdit: null,
      allRecords: [],
      spdCheckbox: null,
      spdDisabled: false,
      workZoneCheckbox: null,
      workZoneDisabled: false,
      privateProperty: false,
      unknownLocation: null,
    };
  }

  handleChange = async (event: React.ChangeEvent<Element>, data) => {
    let value = (event.target as HTMLInputElement).value;
    const target = event.target as HTMLInputElement;
    const currRecords = this.state.allRecords.map(r => ({ ...r }));
    const updatedData = { ...this.state.dataToEdit };
    const rec = currRecords.find(i => i.dataProperty === data.dataProperty);

    // Only modify values if allowValueClearing is turned on and the checkbox is clicked.
    if (data.allowValueClearing && target.type === 'checkbox') {
      // Disable the textbox if clearing is clicked.
      rec.correctedValueEditable = !target.checked;

      // If clearing is clicked, force it to "" so it overwrites the value. If you uncheck the box
      // set it to null so it doesn't overwrite the value.
      value = target.checked ? " " : null;
    }

    if (rec) rec.correctedValue = value;

    if (data.dataProperty === 'locationLine') {
      // The Location Description is persisted on the spot-mapped location's LocationLine
      // (DataEntryService.SaveLocationDataEntry) and is ignored on correctedData. Write the
      // manual edit onto the pin the backend treats as latest (newest CreatedDate, then
      // highest UserTypeId) so it saves and reloads correctly.
      const pins = updatedData.spotMappedLocations || [];
      if (pins.length > 0) {
        const latestPin = pins.reduce((latest, pin) => {
          const latestTime = new Date(latest.createdDate).getTime();
          const pinTime = new Date(pin.createdDate).getTime();
          if (pinTime !== latestTime) return pinTime > latestTime ? pin : latest;
          return (pin.userTypeId || 0) > (latest.userTypeId || 0) ? pin : latest;
        });
        latestPin.locationLine = value;
      }
    } else {
      updatedData.correctedData[data.dataProperty] = value;
    }

    // Only react when the *County* field changed due to user input
    if (data.dropdownGroupName === 'County') {
      const countyId = Number(value) || 0;
      const results = await getCitiesByCountyId(countyId);

      // Find the City field config
      const cityIdx = currRecords.findIndex(r =>
        r.dropdownGroupName === 'City' ||
        r.dataProperty === 'city' ||
        r.dataProperty === 'cityId'
      );

      if (cityIdx > -1) {
        // Clear selection in parent state
        currRecords[cityIdx] = { ...currRecords[cityIdx], correctedValue: '' };
        updatedData.correctedData[currRecords[cityIdx].dataProperty] = '';
      }

      // Commit state first, then update the child options (fromUser = true)
      this.setState({ allRecords: currRecords, dataToEdit: updatedData }, () => {
        const cityRef =
          this.elementRefs.find(r =>
            r?.props?.config?.dropdownGroupName === 'City' ||
            r?.props?.config?.dataProperty === 'city' ||
            r?.props?.config?.dataProperty === 'cityId'
          ) || this.elementRefs[4];

        cityRef?.rebindDropdownValues(results, { fromUser: true });
      });

      return; // early return so we don't set state again below
    }

    // default path
    this.setState({ allRecords: currRecords, dataToEdit: updatedData }, () => {
      this.props.updateDataToSave?.(updatedData);
    });
  };


  getDataToSave = () => {
    return {
      data: this.state.dataToEdit,
      currentItemId: this.props.itemToEdit,
    };
  };

  onSaveLocationData = (data: any, pProperty: boolean, uLocation: boolean) => {    
    let itemToUpdate = this.state.dataToEdit.spotMappedLocations.find(
      (item) => item.userTypeId == this.props.userTypeId
    );
    if (!itemToUpdate) itemToUpdate = data;
    else {
      itemToUpdate = { ...itemToUpdate, ...data };
    }

    let allOtherSpotmaps = this.state.dataToEdit.spotMappedLocations.filter(
      (item) => item.userTypeId != this.props.userTypeId
    );
    allOtherSpotmaps.push(itemToUpdate);

    let dataUpdate = this.state.dataToEdit;
    dataUpdate.spotMappedLocations = allOtherSpotmaps;
    dataUpdate.correctedData.ownership = pProperty ? '02' : '01';

    this.setState(
      {
        dataToEdit: dataUpdate,
        privateProperty: pProperty,
        unknownLocation: uLocation,
      },
      () => {
        this.mapConfig.defaultLocations = this.state.dataToEdit.spotMappedLocations;
        this.mapConfig.privateProperty = this.state.privateProperty;
        this.mapConfig.unknownLocation = this.state.unknownLocation;
      }
    );

    this.props.setContinue(
      this.state.allRecords[7].correctedValue.trim() != ''
    );
  };

  setCheckbox = () => {
    this.setState({ spdCheckbox: !this.state.spdCheckbox });
    this.componentDidMount();
  };

  componentDidMount() {
    Api.post(urls.GET_DATA_FOR_LOCATION_DATA_ENTRY, {
      id: this.props.itemToEdit,
      queueType:
        this.props.currentQueueType == LocationWorkflowStepName.SpotMapping
          ? 1
          : 2,
    }).then((response: ApiResponse<any>) => {
      this.setState({ dataToEdit: response.data }, () => {
        if (!this.state.dataToEdit?.investigatorData) {
          toast.error("Could not get location data");
          return;
        }
        let resultsUnsorted = Object.keys(
          this.state.dataToEdit.investigatorData
        )
          .filter((key) => {
            let current = locationRecordConfigs.find(
              (a) => a.dataProperty == key
            );
            return current != undefined && current.show;
          })
          .map((key) => {
            let current = locationRecordConfigs.find(
              (a) => a.dataProperty == key
            );
            current.originalValue = this.state.dataToEdit.investigatorData[key];
            if (key == 'locationLine')
              // Seed the editable Location Description with the current value from the latest
              // spot-mapped pin so it can be edited in place; edits persist back to that pin's
              // LocationLine (not correctedData) on save.
              current.correctedValue = this.state.dataToEdit.investigatorData[key] ?? '';
            else current.correctedValue = ''; //(this.state.dataToEdit.correctedData != null) ? this.state.dataToEdit.correctedData[key] : "";

            if (
              this.props.currentQueueType == LocationWorkflowStepName.Review &&
              this.state.dataToEdit.investigatorData.ownership == '01' &&
              key == 'streetName'
            ) {
              let spotMappingDetail = this.state.dataToEdit.spotMappedLocations.find(
                (pin) => pin.userTypeId == 2
              );
              let bypassedLRS = spotMappingDetail
                ? spotMappingDetail.skipMeasureDetails
                : true;
              if (bypassedLRS) current.correctedValueEditable = true;
              else current.correctedValueEditable = false;
            }

            if(this.props.userTypeId == eLocationUserType.Administrator 
              && (key == 'routeNumber' || key == 'referencePost')) {
              current.correctedValueEditable = true;
            }

            return current;
          });

        let results: IFormElementConfig[] = _.sortBy(resultsUnsorted, (item) => {
          let matchIndex = _.indexOf(
            locationRecordConfigs.map((a) => a.dataProperty),
            item.dataProperty
          );
          return matchIndex === -1 ? resultsUnsorted.length : matchIndex;
        });

        this.setState(
          {
            allRecords: results,
            privateProperty:
              results.find((a) => a.dataProperty == 'ownership')
                .originalValue == '02',
          },
          () => {
            this.props.setCrashId(this.state.dataToEdit.crashReportId);

            this.mapConfig = {
              reportId: this.state.dataToEdit.reportId,
              privateProperty: this.state.privateProperty,
              unknownLocation: this.state.unknownLocation,
              currentUserId: this.props.currentUserId,
              webServiceUrl: this.props.webserviceUrl,
              readonly: this.props.readOnly,
              defaultLocations: this.state.dataToEdit.spotMappedLocations,
              defaultFocusLocation: this.state.dataToEdit.defaultFocusLocation,
              userTypeId: this.props.userTypeId,
              crashReportId: this.state.dataToEdit.crashReportId,
              onSaveValue: this.onSaveLocationData,
              locationQueue: this.props.currentQueueType,
              getRecordValueByKey: this.getRecordValueByKey,
              setRecordValue: this.setRecordValue,
              setCorrectedValueEditability: this.setCorrectedValueEditability
            };
          }
        );

        if (this.state.spdCheckbox !== null) {
          this.setStatePropertyDamage();
        }
        //Handle StatePropertyDamage Checkbox
        let keywordPropertyDamageIndicator = this.state.dataToEdit
          .investigatorData.keywords.propertyDamageIndicator
          ? this.state.dataToEdit.investigatorData.keywords
            .propertyDamageIndicator
          : '';
        if (keywordPropertyDamageIndicator != '')
          keywordPropertyDamageIndicator.toLowerCase();

        if (keywordPropertyDamageIndicator == 'yes') {
          this.setState({ spdCheckbox: true, workZoneDisabled: true });
        } else {
          this.setState({ spdCheckbox: false });
        }
        //Handle WorkZone Checkbox
        let keywordWorkZoneIndicator = this.state.dataToEdit.investigatorData
          .keywords.workZoneIndicator
          ? this.state.dataToEdit.investigatorData.keywords.workZoneIndicator
          : '';
        if (keywordWorkZoneIndicator != '')
          keywordWorkZoneIndicator.toLowerCase();

        if (keywordWorkZoneIndicator == 'yes') {
          this.setState({ workZoneCheckbox: true, workZoneDisabled: true });
        } else {
          this.setState({ workZoneCheckbox: false });
        }

        if (this.props.updateDataToSave) {
          this.props.updateDataToSave(this.state.dataToEdit);
        }
      });
    });

    if (this.props.validationErrors && this.props.validationErrors.length > 0) {
      console.debug("validation errors sent to location entry: ", this.props.validationErrors);
    }
  }

  setSpdCheckbox = () => {
    this.setState({ spdCheckbox: !this.state.spdCheckbox });
    this.setStatePropertyDamage();
  };

  setStatePropertyDamage = () => {
    if (this.state.dataToEdit.investigatorData.keywords) {
      Api.post(urls.SAVE_STATE_PROPERTY_DAMAGE + 'workflowItemStepId=' + this.props.workflowItemStepId, {
        ...this.state.dataToEdit.investigatorData.keywords,
        propertyDamageIndicator: this.state.spdCheckbox,
      }).then((response: ApiResponse<any>) => {
        if (response.ok) {
        }
      });
    }
  };

  setWorkZoneCheckbox = () => {
    this.setState({
      workZoneCheckbox: !this.state.workZoneCheckbox,
      workZoneDisabled: true,
    });
    this.setWorkZone();
  };

  setWorkZone = () => {
    if (this.state.dataToEdit.investigatorData.keywords) {
      Api.post(urls.SAVE_WORK_ZONE_INDICATOR, {
        ...this.state.dataToEdit.investigatorData.keywords,
        workZoneIndicator: this.state.workZoneCheckbox,
      }).then((response: ApiResponse<any>) => {
        if (response.ok) {
        }
      });
    }
  };

  handleRef = (el: any) => {
    if (el != null && this.elementRefs.indexOf(el) < 0)
      this.elementRefs.push(el);
  };

  getRecordValueByKey = (key: string) => {
    let itemToReturn = this.state.allRecords.find((a) => a.dataProperty == key);
    return itemToReturn.correctedValue
      ? itemToReturn.correctedValue
      : itemToReturn.originalValue;
  };

  setRecordValue = (key: string, value: string, valueType: eValueType) => {
    let allItems = this.state.allRecords;
    let itemToUpdate = allItems.find((a) => a.dataProperty == key);
    let itemToUpdateIndex = allItems.findIndex((a) => a.dataProperty == key);

    if (valueType == eValueType.CalculatedValue)
      itemToUpdate.calculatedValue = value;
    else
      itemToUpdate.correctedValue = value;

    allItems[itemToUpdateIndex] = itemToUpdate;

    this.setState({ allRecords: allItems }, () => {
      this.elementRefs
        .find((a) => a.props.config.dataProperty == key)
        .showCorrectedElement();
      this.forceUpdate();
    });
  };

  setCorrectedValueEditability = (key: string, value: boolean) => {
    let allItems = this.state.allRecords;
    let itemToUpdate = allItems.find((a) => a.dataProperty == key);
    let itemToUpdateIndex = allItems.findIndex((a) => a.dataProperty == key);

    itemToUpdate.correctedValue = "";
    itemToUpdate.correctedValueEditable = value;

    this.setState({ allRecords: allItems }, () => {
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.props.setContinue(false);
  }

  handleOpenMapButton = () => {
    this.props.openMap(this.mapConfig);
  }

  render() {

    let anyPertinentErrors = false;
    const warningIconClass = 'fas fa-exclamation-triangle ttwarning';
    const errorIconClass = 'fa-solid fa-circle-exclamation tterror';
    let iconClass = '';
    if (this.props.validationErrors.some(ve =>
      ve.components.some(c => this.state.allRecords.some(ar => ar.validationFormIoComponents.some(vc => vc == c && ve.type == 'error'))))) {
      anyPertinentErrors = true;
      iconClass = errorIconClass;
    } else {
      iconClass = warningIconClass;
    }

    return (
      <div>
        <div className="divLocationDataEntryMap">
          {this.state.dataToEdit &&
            this.mapConfig &&
            this.props.userTypeId == eLocationUserType.Administrator && (
              <button
                className="btn btn-default"
                onClick={this.handleOpenMapButton}
                style={{
                  margin: 0,
                  padding: 0,
                  width: "100%",
                  height: 50,
                  whiteSpace: "normal",
                  lineHeight: "100%",
                }}
                key="showMapPopupButton"
              >
                Map It
              </button>
            )}
          {this.state.dataToEdit &&
            this.mapConfig &&
            this.props.userTypeId != eLocationUserType.Administrator && (
              <MappingComponent
                mapConfig={this.mapConfig}
                ref={(el) => (this.mapElement = el)}
              />
            )}
        </div>
        {this.state.allRecords && this.state.allRecords.length > 0 && (
          <React.Fragment>
            <Grid>
              <Row>
                <Col sm={4} md={4}>

                  <DualFormElement
                    config={this.state.allRecords[0]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[0])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
                <Col sm={4} md={4}>
                  <DualFormElement
                    config={this.state.allRecords[1]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[1])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
                <Col sm={4} md={4}>
                  <DualFormElement
                    config={this.state.allRecords[2]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[2])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={4} md={4}>
                  <DualFormElement
                    config={this.state.allRecords[3]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[3])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
                <Col sm={4} md={4}>
                  <DualFormElement
                    config={this.state.allRecords[4]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[4])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
                <Col sm={4} md={4}>
                  <DualFormElement
                    config={this.state.allRecords[5]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[5])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={4} md={4}>
                  <DualFormElement
                    config={this.state.allRecords[6]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[6])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
                <Col sm={8} md={8}>
                  <DualFormElement
                    config={this.state.allRecords[7]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[7])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={4} md={4}>
                  <DualFormElement
                    config={this.state.allRecords[8]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[8])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
                <Col sm={4} md={4}>
                  <DualFormElement
                    config={this.state.allRecords[9]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[9])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
                <Col sm={4} md={4}>
                  <DualFormElement
                    config={this.state.allRecords[10]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[10])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={4} md={4}>
                  <DualFormElement
                    config={this.state.allRecords[11]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[11])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
                <Col sm={4} md={4}>
                  <DualFormElement
                    config={this.state.allRecords[12]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[12])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
                <Col sm={4} md={4}>
                  <DualFormElement
                    config={this.state.allRecords[13]}
                    readOnly={false}
                    onChange={(event) =>
                      this.handleChange(event, this.state.allRecords[13])
                    }
                    ref={(el) => this.handleRef(el)}
                    validationErrors={this.props.validationErrors}
                    hasAnyFormErrors={anyPertinentErrors}
                  />
                </Col>
              </Row>
            </Grid>
          </React.Fragment>
        )}
        {this.props.userTypeId == eLocationUserType.Administrator &&
          this.props.validatingLocationData && (
            <div className="divFooterLocationValidatingLoading align-self-center">
              Validating {"   "}
              <img
                src={require("../../../../../src/assets/SmartAdmin/img/32/Marty.gif")}
              />
              <img
                src={require("../../../../assets/SmartAdmin/img/32/Marty.gif")}
              />
            </div>
          )}
        {this.props.userTypeId == eLocationUserType.Administrator &&
          !this.props.validatingLocationData && (
            <React.Fragment>
              <Grid>
                <Row className="justify-content-end">
                  <Col sm={1} className="justify-content-end align-self-center">
                    {this.props.validationErrors &&
                      this.props.validationErrors.length > 0 && (
                        <React.Fragment>
                          <TooltipToggle
                            placment="top"
                            validations={this.props.validationErrors}
                            iconClass={iconClass}
                          ></TooltipToggle>
                        </React.Fragment>
                      )}
                  </Col>
                  <Col sm={8}>
                    {this.props.validationErrors &&
                      this.props.validationErrors.length > 0 && (
                        <React.Fragment>
                          <div className="divFooterLocationValidations">
                            <ul>
                              {this.props.validationErrors.map((x, idx) => (
                                <li
                                  key={idx}
                                  className={
                                    x.type == "error"
                                      ? "loc-val-error"
                                      : "loc-val-warning"
                                  }
                                >
                                  {x.message}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </React.Fragment>
                      )}
                  </Col>
                  <Col sm={3} className="align-self-center">
                    <div className="divFooterLocationButtons">
                      <Button
                        variant="warning"
                        onClick={() => this.props.validateData()}
                      >
                        Validate
                      </Button>
                      {"   "}
                      <Button
                        variant="success"
                        onClick={() => this.props.handleSaveLocationEntry()}
                        disabled={false}
                      >
                        Save
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Grid>
            </React.Fragment>
          )}
      </div>
    );
  }
}

export function mapStateToProps(state: any) {
  return {
    webServiceURL: state.Application.backendURL,
    currentUser: state.Application.currentUser,
  };
}

const mapDispatchToProps = {

};

export default connect(mapStateToProps, mapDispatchToProps)(LocationDataEntry);